/**
 * iter 167 — Browser-stable device fingerprint.
 *
 * The "fingerprint" is a high-entropy UUID generated on first visit and
 * persisted in localStorage. It is browser-scoped (NOT cross-device) and
 * survives refresh, tab-close, browser restart, and OS reboot — exactly
 * matching the product spec for "trust this device".
 *
 * Privacy note: this is intentionally NOT a canvas/font/audio fingerprint.
 * Those are anti-privacy and unreliable. A stored random UUID is far more
 * stable, fully privacy-preserving, and the user can wipe it via the
 * standard browser site-data clear.
 *
 * Server-side the raw fingerprint is never stored — only a peppered
 * SHA-256 hash (see /app/backend/services/trusted_devices.py).
 */
const FP_KEY = 'rf_device_fp';

function _uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return 'fp-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36) +
    '-' + Math.random().toString(36).slice(2);
}

export function getOrCreateDeviceFingerprint() {
  try {
    let fp = localStorage.getItem(FP_KEY);
    if (!fp) {
      fp = _uuid();
      localStorage.setItem(FP_KEY, fp);
    }
    return fp;
  } catch (_e) {
    // localStorage blocked — fall back to in-memory (session only).
    if (!window.__rf_inmem_fp) window.__rf_inmem_fp = _uuid();
    return window.__rf_inmem_fp;
  }
}

export function clearDeviceFingerprint() {
  try { localStorage.removeItem(FP_KEY); } catch (_e) {}
}

/**
 * Best-effort human-friendly device label from navigator.userAgent.
 * Used only for the admin "Trusted Devices" management UI.
 */
export function getDeviceLabel() {
  const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '';
  let browser = 'Browser';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) browser = 'Chrome';
  else if (/Firefox\//i.test(ua)) browser = 'Firefox';
  else if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari';
  else if (/Opera|OPR\//i.test(ua)) browser = 'Opera';
  let os = 'Device';
  if (/Windows NT/i.test(ua)) os = 'Windows';
  else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';
  else if (/Android/i.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iOS/i.test(ua)) os = 'iOS';
  else if (/Linux/i.test(ua)) os = 'Linux';
  return `${browser} on ${os}`;
}
